import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Clock, Phone } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { fadeUp } from "../lib/motion";
import { SERVICES } from "../lib/data";
import { getSavedRegion, getCurrencyForRegion } from "../lib/currency";
import { BUDGET_OPTIONS } from "../lib/data";

export default function Contact() {
  const [region, setRegion] = useState(getSavedRegion());
  const currency = getCurrencyForRegion(region);

  useEffect(() => {
    const handleRegionChange = (event) => setRegion(event.detail.region);
    window.addEventListener("solksites-region-change", handleRegionChange);
    return () => window.removeEventListener("solksites-region-change", handleRegionChange);
  }, []);

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", service: "", budget: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const budgetOption = BUDGET_OPTIONS.find((opt) => opt.value === form.budget);
      const budgetLabel = budgetOption ? budgetOption.label[currency] : form.budget;

      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === "budget") return; // handle separately below
        if (value) formData.append(key, value);
      });
      if (form.budget) formData.append("budget", budgetLabel);

      formData.append("access_key", "647c7261-878e-48a8-8ea5-a914d121d789");
      formData.append("source_page", "contact");

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        setSent(true);
        toast.success("Message sent. We'll reply within one business day.");
        setForm({ name: "", email: "", company: "", phone: "", service: "", budget: "", message: "" });
      } else {
        toast.error("Your message could not be sent. Please try again.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's talk."
        subtitle="Tell us a bit about your business and what you're hoping to build. We'll do our best to reply within 1 business day."
      />

      <section className="pb-32" data-testid="contact-section">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-5 space-y-8">
            <div>
              <p className="eyebrow">Prefer email?</p>
              <a href="mailto:hello@solksites.com" className="mt-3 flex items-center gap-3 text-lg font-semibold text-slate-900 dark:text-white hover:text-blue-600 transition-colors" data-testid="contact-email">
                <Mail className="h-5 w-5" /> inquiries@solksites.com
              </a>
            </div>
            <div>
              <p className="eyebrow">Or phone?</p>
              <a href="https://wa.me/14383543607?text=Hi%20Charlie%20I%20was%20looking%20at%20your%20website%20and%20would%20be%20interested%20in%20updating%2Fcreating%20my%20website%20with%20yourself%20through%20SolkSites%21" className="btn-primary mt-2.5" target="_blank" rel="noopener noreferrer" data-testid="cta-primary-button">
                WhatsApp us
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div>
              <p className="eyebrow">Or conference call?</p>
              <a href="https://cal.com/solksites/chat-with-us" className="btn-primary mt-2.5" target="_blank" rel="noopener noreferrer" data-testid="cta-primary-button">
                Book on Cal.com
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="card-surface p-8 md:p-10">
              {sent ? (
                <div className="text-center py-16" data-testid="contact-success">
                  <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">✓</div>
                  <h3 className="mt-6 text-2xl font-bold text-slate-900 dark:text-white">Thanks — message received.</h3>
                  <p className="mt-3 text-slate-600 dark:text-slate-400">We'll reply within one business day. Usually much sooner.</p>
                  <button onClick={() => setSent(false)} className="btn-secondary mt-8">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Your name</span>
                      <input required name="name" value={form.name} onChange={set("name")} type="text" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 dark:focus:ring-blue-500/20" data-testid="contact-name" />
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email</span>
                      <input required name="email" value={form.email} onChange={set("email")} type="email" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="contact-email-input" />
                    </label>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Company <span className="text-slate-400 font-normal">(optional)</span></span>
                      <input name="company" value={form.company} onChange={set("company")} type="text" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="contact-company" />
                    </label>
                    <label className="block">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Phone <span className="text-slate-400 font-normal">(optional)</span></span>
                      <input name="phone" value={form.phone} onChange={set("phone")} type="tel" className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="contact-phone-input" />
                    </label>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">What are you interested in?</span>
                      <select name="service" value={form.service} onChange={set("service")} className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100" data-testid="contact-service">
                        <option value="">Choose a service</option>
                        {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                        <option value="Not sure yet">Not sure yet</option>
                      </select>
                    </label>
                      <label className="block">
                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Budget</span>
                        <select
                          name="budget"
                          value={form.budget}
                          onChange={set("budget")}
                          className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          data-testid="contact-budget"
                        >
                          <option value="">Select a range</option>
                          {BUDGET_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label[currency]}
                            </option>
                          ))}
                        </select>
                      </label>
                  </div>
                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tell us about your project</span>
                    <textarea required name="message" value={form.message} onChange={set("message")} rows={5} className="mt-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-4 py-3 text-sm focus:border-blue-600 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none" placeholder="A few lines about what you'd like to build or improve." data-testid="contact-message" />
                  </label>
                  <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto disabled:opacity-60" data-testid="contact-submit">
                    {loading ? "Sending…" : "Send message"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Your info stays with us — we don't share it.</p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
